import React, { useMemo } from 'react';
import { Box, Button, Flex, HStack, List, ListItem, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import FeatureIcon from '../../components/FeatureIcon/FeatureIcon.tsx';
import { ResidentFeature } from '../../constants/ResidentFeature.ts';
import { parsePrefixedText } from '../../utils/parsePrefixedText.ts';

interface FeatureActivationCardProps {
  feature: ResidentFeature;
  title: string;
  description: string;
  footerNote?: string;
  question: string;
  value: boolean | null;
  onYes: () => void;
  onNo: () => void;
  disabled?: boolean;
}

const ACTIVE_COLOR = '#7454FF';
const NO_SELECTED_COLOR = '#2B4956';
const INACTIVE_BORDER = '#DCDCDC';

const FeatureActivationCard: React.FC<FeatureActivationCardProps> = ({
  feature,
  title,
  description,
  footerNote,
  question,
  value,
  onYes,
  onNo,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const isYesSelected = value === true;
  const isNoSelected = value === false;

  const cardBorder = isYesSelected
    ? `2px solid ${ACTIVE_COLOR}`
    : isNoSelected
      ? '2px solid #C6C6C6'
      : '2px solid transparent';

  const cardBg = isYesSelected
    ? 'rgba(116, 84, 255, 0.05)'
    : isNoSelected
      ? '#F3F2F2'
      : '#FAF9F9';
  const contentColor = isNoSelected ? '#C6C6C6' : '#000';
  const yesIconColor = isYesSelected ? 'white' : '#2B4956';
  const noIconColor = isNoSelected ? 'white' : '#2B4956';
  const parsedDescription = useMemo(
    () => parsePrefixedText(description),
    [description]
  );

  const getYesButtonDisabledStyles = () => ({
    bg: isYesSelected ? ACTIVE_COLOR : 'white',
    color: isYesSelected ? 'white' : 'alea.500',
    borderColor: isYesSelected ? ACTIVE_COLOR : INACTIVE_BORDER,
    opacity: 1,
    cursor: 'not-allowed',
  });

  const getNoButtonDisabledStyles = () => ({
    bg: isNoSelected ? NO_SELECTED_COLOR : 'white',
    color: isNoSelected ? 'white' : 'alea.500',
    borderColor: isNoSelected ? NO_SELECTED_COLOR : INACTIVE_BORDER,
    opacity: 1,
    cursor: 'not-allowed',
  });

  return (
    <Flex
      bg={cardBg}
      borderRadius="16px"
      border={cardBorder}
      p="24px"
      gap={{ base: '20px', lg: '24px' }}
      direction={{ base: 'column', lg: 'row' }}
      align={{ base: 'stretch', lg: 'flex-start' }}
      opacity={disabled ? (isYesSelected ? 0.4 : 0.5) : 1}
      transition="border-color 0.2s ease, background-color 0.2s ease"
    >
      <Flex
        flexShrink={0}
        w="50px"
        h="50px"
        bg="white"
        borderRadius="12px"
        align="center"
        justify="center"
      >
        <FeatureIcon
          title={feature}
          active={true}
          customColor={isNoSelected ? '#C6C6C6' : ACTIVE_COLOR}
          label=""
        />
      </Flex>

      <Box flex="1" minW={0}>
        <Text fontSize="20px" fontWeight={500} color={contentColor} mb="12px">
          {title}
        </Text>
        {parsedDescription.plainText ? (
          <Text fontSize="16px" color={contentColor} whiteSpace="pre-line">
            {parsedDescription.plainText}
          </Text>
        ) : (
          <>
            <List spacing="2px" styleType="disc" ml="40px">
              {parsedDescription.bullets.map((bullet) => (
                <ListItem key={bullet} fontSize="16px" color={contentColor}>
                  {bullet}
                </ListItem>
              ))}
            </List>
            {parsedDescription.trailingText && (
              <Text
                mt="8px"
                fontSize="16px"
                fontWeight={500}
                color={contentColor}
                whiteSpace="pre-line"
              >
                {parsedDescription.trailingText}
              </Text>
            )}
          </>
        )}
        {footerNote && (
          <Text
            mt="10px"
            fontSize="16px"
            fontWeight={500}
            color={contentColor}
            whiteSpace="pre-line"
          >
            {footerNote}
          </Text>
        )}
      </Box>

      <Flex
        direction="column"
        align="flex-start"
        flexShrink={0}
        minW={{ lg: '220px' }}
      >
        <Text
          fontSize="15px"
          fontWeight={300}
          color="alea.500"
          textAlign="left"
          mb="5px"
        >
          {question}
        </Text>
        <Flex gap="10px">
          <Button
            variant="outline"
            size="md"
            w="170px"
            h="50px"
            borderRadius="20px"
            borderColor={isYesSelected ? ACTIVE_COLOR : INACTIVE_BORDER}
            bg={isYesSelected ? ACTIVE_COLOR : 'white'}
            color={isYesSelected ? 'white' : 'alea.500'}
            fontWeight={400}
            fontSize="18px"
            onClick={onYes}
            isDisabled={disabled}
            _hover={{
              bg: isYesSelected ? ACTIVE_COLOR : '#FAFAFA',
              borderColor: isYesSelected ? ACTIVE_COLOR : INACTIVE_BORDER,
            }}
            _disabled={getYesButtonDisabledStyles()}
          >
            <HStack spacing="3px">
              <Box
                as="span"
                display="inline-flex"
                flexShrink={0}
                alignItems="center"
                justifyContent="center"
                w="25px"
                h="25px"
                aria-hidden
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="11"
                  viewBox="0 0 15 11"
                  fill="none"
                >
                  <path
                    d="M1 6.75L4.77067 10"
                    stroke={yesIconColor}
                    strokeWidth="2"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                  />
                  <path
                    d="M4.77344 10L13.765 1"
                    stroke={yesIconColor}
                    strokeWidth="2"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                  />
                </svg>
              </Box>
              <Text color="inherit" fontSize="18px" fontWeight={400}>
                {t('yesActivate')}
              </Text>
            </HStack>
          </Button>
          <Button
            variant="outline"
            size="md"
            w="170px"
            h="50px"
            borderRadius="20px"
            borderColor={isNoSelected ? NO_SELECTED_COLOR : INACTIVE_BORDER}
            bg={isNoSelected ? NO_SELECTED_COLOR : 'white'}
            color={isNoSelected ? 'white' : 'alea.500'}
            fontWeight={400}
            fontSize="18px"
            onClick={onNo}
            isDisabled={disabled}
            _hover={{
              bg: isNoSelected ? NO_SELECTED_COLOR : '#FAFAFA',
              borderColor: isNoSelected ? NO_SELECTED_COLOR : INACTIVE_BORDER,
            }}
            _disabled={getNoButtonDisabledStyles()}
          >
            <HStack spacing="3px">
              <Box
                as="span"
                display="inline-flex"
                flexShrink={0}
                alignItems="center"
                justifyContent="center"
                w="25px"
                h="25px"
                aria-hidden
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                  fill="none"
                >
                  <path
                    d="M1 13.5L13.5 1"
                    stroke={noIconColor}
                    strokeWidth="2"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                  />
                  <path
                    d="M13.5 13.5L1 1"
                    stroke={noIconColor}
                    strokeWidth="2"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                  />
                </svg>
              </Box>
              <Text color="inherit" fontSize="18px" fontWeight={400}>
                {t('no')}
              </Text>
            </HStack>
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default FeatureActivationCard;
